import { useEffect, useState, useCallback, useRef } from "react";
import * as Icons from "lucide-react";
import axios from "axios";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AdminActionDialog } from "@/components/AdminActionDialog";
import { toast } from "sonner";
import { ExportButton } from "@/components/ExportDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const disp = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("en-GB", { timeZone: "Asia/Kolkata" }); } catch { return iso; } };

const SECTIONS = [
  { key: "first_aid", title: "First Aid Box", count: 1, toggle: "first_aid_stocked", toggleLabel: "Stocked and Ready", icon: "HeartPulse", hints: ["First aid box"] },
  { key: "fire_safety", title: "Fire Safety", count: 2, icon: "Flame", hints: ["Fire equipment", "Fire point"] },
  { key: "harness", title: "Labor Harness Check", count: 1, toggle: "harness_secured", toggleLabel: "Harness Secured", icon: "LifeBuoy", hints: ["Harness in use"] },
  { key: "safety_gears", title: "Labor Safety Gears", count: 3, icon: "HardHat", hints: ["Helmet", "Safety boots", "Gloves"] },
  { key: "supervisor", title: "Supervisor Check", count: 1, icon: "UserCheck", hints: ["Supervisor in highlighted vest"] },
];
const SECTION_MAP = Object.fromEntries(SECTIONS.map((s) => [s.key, s]));
const REQUIRED_TOTAL = SECTIONS.reduce((a, s) => a + s.count, 0);

function getGeo() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  });
}

function SafetyPhoto({ path, label }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u; if (!path) return;
    api.get(`/safety/photo/${path}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [path]);
  return url ? <img src={url} alt={label} className="w-full rounded-lg border bg-secondary/30" data-testid="safety-photo-img" />
    : <div className="aspect-video rounded-lg border bg-secondary/30 flex items-center justify-center text-xs text-muted-foreground">Loading…</div>;
}

function CaptureFlow({ onDone, onCancel }) {
  const [bySection, setBySection] = useState(() => Object.fromEntries(SECTIONS.map((s) => [s.key, []])));
  const [toggles, setToggles] = useState({ first_aid_stocked: false, harness_secured: false });
  const [busyKey, setBusyKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const activeRef = useRef(null);
  const camRef = useRef(); const upRef = useRef();

  useEffect(() => () => {
    Object.values(bySection).flat().forEach((p) => { if (p.preview) URL.revokeObjectURL(p.preview); });
  }, []); // eslint-disable-line

  const total = Object.values(bySection).reduce((a, arr) => a + arr.length, 0);
  const complete = SECTIONS.every((s) => bySection[s.key].length === s.count);

  const addFile = async (file) => {
    const key = activeRef.current;
    if (!file || !key) return;
    const sec = SECTION_MAP[key];
    if (bySection[key].length >= sec.count) { toast.error(`${sec.title} already has ${sec.count} photo(s)`); return; }
    setBusyKey(key);
    let geo = await getGeo();
    if (!geo) { toast.message("Location not detected — allow location and retry, or continue without it."); geo = await getGeo(); }
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (geo) { fd.append("lat", geo.lat); fd.append("lng", geo.lng); fd.append("accuracy", geo.accuracy); }
      fd.append("captured_at", new Date().toISOString());
      const token = localStorage.getItem("bsp_token");
      const r = await axios.post(`${API}/safety/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setBySection((prev) => ({ ...prev, [key]: [...prev[key], { ...r.data, category: key, preview: URL.createObjectURL(file) }] }));
      if (!geo) toast.warning("Photo added without GPS (location not shared)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusyKey(null); }
  };

  const trigger = (key, ref) => { activeRef.current = key; ref.current?.click(); };
  const remove = (key, i) => setBySection((prev) => {
    const arr = prev[key]; const p = arr[i]; if (p?.preview) URL.revokeObjectURL(p.preview);
    return { ...prev, [key]: arr.filter((_, j) => j !== i) };
  });

  const submit = async () => {
    if (!complete) return toast.error("Please upload all required photos first");
    setSubmitting(true);
    try {
      const photos = Object.values(bySection).flat().map((p) => ({
        category: p.category, storage_path: p.storage_path, caption: p.caption || "",
        lat: p.lat, lng: p.lng, accuracy: p.accuracy, captured_at: p.captured_at, location_shared: p.location_shared,
      }));
      await api.post("/safety/checks", { photos, ...toggles });
      toast.success("Morning safety check submitted");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-bold">New Morning Safety Check</h2>
          <p className="text-sm text-muted-foreground">Complete every section. Each photo is stamped with date, time (IST) &amp; GPS at upload.</p>
        </div>
        <Badge variant="secondary" className={complete ? "bg-emerald-100 text-emerald-800" : ""} data-testid="safety-photo-counter">{total}/{REQUIRED_TOTAL} required photos</Badge>
      </div>

      {SECTIONS.map((s) => {
        const arr = bySection[s.key];
        const SecIcon = Icons[s.icon] || Icons.Camera;
        const done = arr.length === s.count;
        return (
          <div key={s.key} className={`rounded-xl border p-4 ${done ? "border-emerald-300 bg-emerald-50/40" : "border-border"}`} data-testid={`safety-section-${s.key}`}>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${done ? "bg-emerald-600 text-white" : "bg-secondary text-foreground"}`}>
                  {done ? <Icons.Check className="h-4 w-4" /> : <SecIcon className="h-4 w-4" />}
                </div>
                <div>
                  <div className="font-semibold text-sm">{s.title}</div>
                  <div className="text-xs text-muted-foreground">{arr.length}/{s.count} photo{s.count > 1 ? "s" : ""} · {s.hints.join(", ")}</div>
                </div>
              </div>
              {s.toggle && (
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`safety-toggle-${s.key}`}>
                  <Switch checked={toggles[s.toggle]} onCheckedChange={(v) => setToggles((t) => ({ ...t, [s.toggle]: v }))} data-testid={`safety-toggle-switch-${s.key}`} />
                  <span>{s.toggleLabel}</span>
                </label>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {arr.map((p, i) => (
                <div key={p.storage_path || i} className="rounded-lg border p-2 space-y-2" data-testid={`safety-photo-${s.key}-${i}`}>
                  <div className="relative">
                    <img src={p.preview} alt={`${s.title} ${i + 1}`} className="w-full h-28 object-cover rounded-md" />
                    <button onClick={() => remove(s.key, i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid={`safety-remove-${s.key}-${i}`}><Icons.X className="h-3.5 w-3.5" /></button>
                    <span className={`absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 rounded ${p.location_shared ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>{p.location_shared ? "GPS ✓" : "No GPS"}</span>
                  </div>
                </div>
              ))}
              {arr.length < s.count && (
                <div className="rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 p-3 min-h-[132px]">
                  {busyKey === s.key ? <Icons.Loader2 className="h-6 w-6 animate-spin text-primary" /> : <SecIcon className="h-6 w-6 text-muted-foreground" />}
                  <div className="flex flex-col gap-1.5 w-full">
                    <Button size="sm" onClick={() => trigger(s.key, camRef)} disabled={!!busyKey} data-testid={`safety-take-${s.key}`}><Icons.Camera className="h-4 w-4 mr-1.5" />Take</Button>
                    <Button size="sm" variant="outline" onClick={() => trigger(s.key, upRef)} disabled={!!busyKey} data-testid={`safety-upload-${s.key}`}><Icons.Upload className="h-4 w-4 mr-1.5" />Upload</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFile(e.target.files?.[0]); e.target.value = ""; }} />
      <input ref={upRef} type="file" accept="image/*" className="hidden" onChange={(e) => { addFile(e.target.files?.[0]); e.target.value = ""; }} />

      <div className="flex gap-2 pt-1 sticky bottom-0 bg-background py-2">
        <Button onClick={submit} disabled={submitting || !complete} className="gap-1.5" data-testid="safety-submit-check">
          {submitting ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CheckCircle2 className="h-4 w-4" />}
          {complete ? "Submit Check" : `Upload all ${REQUIRED_TOTAL} photos to submit`}
        </Button>
        <Button variant="outline" onClick={onCancel} data-testid="safety-cancel">Cancel</Button>
      </div>
    </div>
  );
}

export default function SafetyCheck() {
  const { can, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [photoAction, setPhotoAction] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!can("safety", "view")) { setLoading(false); return; }
    setLoading(true);
    Promise.all([api.get("/safety/checks"), api.get("/safety/today")])
      .then(([l, t]) => { setItems(l.data); setToday(t.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const deletePhoto = async (reason) => {
    if (!photoAction || !detail) return;
    setBusy(true);
    try {
      const r = await api.post(`/safety/checks/${detail.id}/delete-photo`, { storage_path: photoAction.storage_path, reason });
      setDetail(r.data);
      setPhotoAction(null);
      toast.success("Photo removed and logged");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not delete photo"); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (row) => {
    try { const r = await api.get(`/safety/checks/${row.id}/pdf`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `safety_${row.check_date}.pdf`; a.click(); URL.revokeObjectURL(u); } catch { toast.error("Could not generate PDF"); }
  };
  const printPdf = async (row) => {
    try { const r = await api.get(`/safety/checks/${row.id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch { toast.error("Could not open PDF"); }
  };

  if (!can("safety", "view"))
    return (
      <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
        <Icons.Lock className="h-8 w-8 mx-auto text-destructive mb-3" />
        <h2 className="font-display text-xl font-bold">Access Restricted</h2>
        <p className="text-muted-foreground text-sm mt-2">You don't have the Morning Safety Check permission.</p>
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#f59e0b1a", color: "#b45309" }}><Icons.ShieldAlert className="h-5 w-5" /></div>
          <div><h1 className="font-display text-2xl font-bold tracking-tight">Morning Safety Check</h1><p className="text-sm text-muted-foreground">Project GPI · daily geo &amp; time-stamped photo log</p></div>
        </div>
        <div className="flex gap-2">
          {can("safety", "export") && <ExportButton report="safety" title="Morning Safety Check Register" testid="safety-export-btn" />}
          {can("safety", "create") && !today?.submitted && !creating && <Button className="gap-1.5" onClick={() => setCreating(true)} data-testid="safety-new-btn"><Icons.Plus className="h-4 w-4" />New Morning Check</Button>}
        </div>
      </div>

      {!creating && today && (
        <div className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${today.submitted ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="safety-today-banner">
          {today.submitted ? <Icons.CheckCircle2 className="h-4 w-4" /> : <Icons.AlertTriangle className="h-4 w-4" />}
          <span>{today.submitted ? `Today's morning safety check is done (${today.check?.photo_count} photos).` : "Today's morning safety check has not been uploaded yet."}</span>
        </div>
      )}

      {creating ? (
        <Card className="p-5"><CaptureFlow onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} /></Card>
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
              {["Date", "Submitted By", "Photos", "With GPS", "Submitted At (IST)", ""].map((h, i) => <TableHead key={i} className="overline text-muted-foreground whitespace-nowrap">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No safety checks yet.</TableCell></TableRow>}
              {!loading && items.map((r) => (
                <TableRow key={r.id} className="hover:bg-secondary/40">
                  <TableCell><button className="text-primary hover:underline font-medium" onClick={() => setDetail(r)} data-testid={`safety-row-${r.id}`}>{r.check_date}</button></TableCell>
                  <TableCell>{r.submitted_by_name || "—"}</TableCell>
                  <TableCell>{r.photo_count}</TableCell>
                  <TableCell>{r.with_location}/{r.photo_count}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{disp(r.created_at)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDetail(r)} data-testid={`safety-view-${r.id}`}><Icons.Eye className="h-4 w-4" /></Button>
                    {can("safety", "download") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => printPdf(r)} data-testid={`safety-print-${r.id}`}><Icons.Printer className="h-4 w-4" /></Button>}
                    {can("safety", "download") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadPdf(r)} data-testid={`safety-pdf-${r.id}`}><Icons.FileDown className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (<>
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2 flex-wrap">Safety Check · {detail.check_date}<Badge variant="secondary">{detail.photo_count} photos</Badge></DialogTitle></DialogHeader>
            <div className="text-sm text-muted-foreground">By {detail.submitted_by_name} · {disp(detail.created_at)}</div>
            {(("first_aid_stocked" in detail) || ("harness_secured" in detail)) && (
              <div className="flex flex-wrap gap-2 mt-1" data-testid="safety-detail-toggles">
                <Badge variant="secondary" className={detail.first_aid_stocked ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>First Aid: {detail.first_aid_stocked ? "Stocked & Ready" : "Not Ready"}</Badge>
                <Badge variant="secondary" className={detail.harness_secured ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>Harness: {detail.harness_secured ? "Secured" : "Not Secured"}</Badge>
              </div>
            )}
            {(() => {
              const photos = detail.photos || [];
              const hasCats = photos.some((p) => p.category);
              const renderPhoto = (p, key, idx) => (
                <div key={key} className="space-y-1.5">
                  <SafetyPhoto path={p.storage_path} label={`Photo ${idx + 1}`} />
                  {p.caption ? <div className="text-sm font-medium">{p.caption}</div> : null}
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">{p.location_shared ? `GPS ${Number(p.lat).toFixed(5)}, ${Number(p.lng).toFixed(5)}` : "Location not shared"}</div>
                    {isAdmin && <button onClick={() => setPhotoAction({ storage_path: p.storage_path })} className="text-[11px] text-destructive hover:underline inline-flex items-center gap-1" data-testid={`safety-delete-photo-${key}`}><Icons.Trash2 className="h-3 w-3" />Delete</button>}
                  </div>
                </div>
              );
              if (!hasCats) {
                return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">{photos.map((p, i) => renderPhoto(p, i, i))}</div>;
              }
              return (
                <div className="space-y-4 mt-2">
                  {SECTIONS.map((s) => {
                    const sec = photos.filter((p) => p.category === s.key);
                    if (!sec.length) return null;
                    return (
                      <div key={s.key} data-testid={`safety-detail-section-${s.key}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                          <h3 className="font-semibold text-sm">{s.title}</h3>
                          {s.toggle && <Badge variant="secondary" className={`text-[10px] ${detail[s.toggle] ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{s.toggleLabel}: {detail[s.toggle] ? "Yes" : "No"}</Badge>}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{sec.map((p, i) => renderPhoto(p, `${s.key}-${i}`, i))}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <div className="flex gap-2 mt-4">
              {can("safety", "download") && <Button variant="outline" className="gap-1.5" onClick={() => printPdf(detail)} data-testid="safety-detail-print"><Icons.Printer className="h-4 w-4" />Print</Button>}
              {can("safety", "download") && <Button variant="outline" className="gap-1.5" onClick={() => downloadPdf(detail)} data-testid="safety-detail-pdf"><Icons.FileDown className="h-4 w-4" />Download PDF</Button>}
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      <AdminActionDialog open={!!photoAction} onOpenChange={(o) => { if (!o) setPhotoAction(null); }}
        title="Delete Photo" description="Removes this photo from the safety check. The record and its other photos are kept."
        confirmLabel="Delete Photo" destructive loading={busy} onConfirm={deletePhoto} />
    </div>
  );
}
